import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import classes from '../main.module.css';

const SECTION_ICONS = {
    overview: '👋',
    introduction: '👋',
    'data-collected': '🗂️',
    collection: '🗂️',
    'how-we-use': '⚙️',
    'log-data': '📊',
    'cloud-sync': '☁️',
    'local-storage': '💾',
    permissions: '🔐',
    'third-party': '🔗',
    links: '🔗',
    analytics: '📊',
    security: '🛡️',
    children: '👶',
    'your-rights': '✅',
    responsibilities: '📋',
    liability: '⚖️',
    changes: '📋',
    deletion: '🗑️',
    'ip-rights': '💡',
    'contact-us': '✉️',
    contact: '✉️',
};

function getIcon(id, fallback) {
    const key = Object.keys(SECTION_ICONS).find(k => id?.toLowerCase().includes(k));
    return key ? SECTION_ICONS[key] : (fallback || '📄');
}

function AppPolicy() {
    const { appName } = useParams();
    const [policyData, setPolicyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const observerRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(true);
        setError(false);

        fetch(`/policies/${appName}.json`)
            .then(res => {
                if (!res.ok) throw new Error('Policy not found');
                return res.json();
            })
            .then(data => {
                setPolicyData(data);
                setActiveSection(data.sections?.[0]?.id || null);
                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    }, [appName]);

    // Intersection observer to track active section
    useEffect(() => {
        if (!policyData) return;
        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) setActiveSection(entry.target.id);
                });
            },
            { rootMargin: '-20% 0px -70% 0px' }
        );

        policyData.sections.forEach(s => {
            const el = document.getElementById(s.id);
            if (el) observerRef.current.observe(el);
        });

        return () => observerRef.current?.disconnect();
    }, [policyData]);

    const handleSidebarClick = (id) => {
        setActiveSection(id);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (loading) {
        return (
            <div className={classes.APContainer}>
                <div className={classes.APSkeletonWrap}>
                    <div className={classes.APSkeletonBadge} />
                    <div className={classes.APSkeletonTitle} />
                    <div className={classes.APSkeletonSub} />
                    <div className={classes.APSkeletonMeta} />
                    <div className={classes.APSkeletonGrid}>
                        {[1, 2, 3].map(i => (
                            <div key={i} className={classes.APSkeletonCard} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={classes.APErrorWrap}>
                <div className={classes.APErrorIcon}>⚠️</div>
                <h1>Policy Not Found</h1>
                <p>We couldn't find a privacy policy for <strong>"{appName}"</strong>.</p>
            </div>
        );
    }

    return (
        <div className={classes.APContainer}>

            {/* Ambient glow */}
            <div className={classes.APGlow} aria-hidden="true" />

            {/* Sticky mini-header */}
            <header className={classes.APStickyHeader}>
                <div className={classes.APHeaderLogo}>
                    <span className={classes.APHeaderLogoText}>
                        {appName ? appName.charAt(0).toUpperCase() + appName.slice(1) : 'App'}
                        <span> by Feline</span>
                    </span>
                </div>
            </header>

            {/* Hero */}
            <div className={classes.APHero}>
                <span className={classes.APBadge}>Privacy &amp; Legal</span>
                <h1 className={classes.APTitle}>{policyData.title}</h1>
                <p className={classes.APDesc}>{policyData.appDescription}</p>
                <div className={classes.APMeta}>
                    <span className={classes.APMetaDot} />
                    Last updated: {policyData.lastUpdated}
                </div>
            </div>

            {/* Body layout */}
            <div className={classes.APLayout}>

                {/* Sidebar TOC */}
                <aside className={classes.APSidebar}>
                    <div className={classes.APSidebarCard}>
                        <p className={classes.APSidebarLabel}>Table of Contents</p>
                        <ul className={classes.APTocList}>
                            {policyData.sections.map((section) => (
                                <li key={section.id}>
                                    <button
                                        className={`${classes.APTocItem} ${activeSection === section.id ? classes.APTocItemActive : ''}`}
                                        onClick={() => handleSidebarClick(section.id)}
                                    >
                                        <span className={classes.APTocIcon}>
                                            {getIcon(section.id)}
                                        </span>
                                        <span>{section.heading}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>

                        {policyData.contact?.email && (
                            <div className={classes.APSidebarContact}>
                                <p>Need help?</p>
                                <a href={`mailto:${policyData.contact.email}`}>
                                    {policyData.contact.email}
                                </a>
                            </div>
                        )}
                    </div>
                </aside>

                {/* Main content */}
                <main className={classes.APMain}>
                    {policyData.sections.map((section, idx) => (
                        <section
                            key={section.id}
                            id={section.id}
                            className={classes.APCard}
                            data-aos="fade-up"
                            data-aos-delay={idx * 40}
                        >
                            <div className={classes.APCardHeader}>
                                <div className={classes.APCardIcon}>
                                    {getIcon(section.id, '📄')}
                                </div>
                                <h2 className={classes.APCardTitle}>{section.heading}</h2>
                            </div>

                            {section.content && (
                                <p className={classes.APCardContent}>{section.content}</p>
                            )}

                            {section.list && (
                                <ul className={classes.APCardList}>
                                    {section.list.map((item, i) => (
                                        <li key={i}>
                                            <span className={classes.APCardListDot} />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    ))}

                    {/* Contact CTA */}
                    {policyData.contact?.email && (
                        <div className={classes.APContactCard} data-aos="fade-up">
                            <h2>Get In Touch</h2>
                            <p>If you have questions about this policy, we're here to help.</p>
                            <a
                                href={`mailto:${policyData.contact.email}`}
                                className={classes.APContactBtn}
                            >
                                {policyData.contact.email}
                            </a>
                            {policyData.contact.phone && policyData.contact.phone !== 'N/A' && (
                                <a
                                    href={`tel:${policyData.contact.phone}`}
                                    className={classes.APContactBtnOutline}
                                >
                                    {policyData.contact.phone}
                                </a>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}

export default AppPolicy;
